import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { supabase } from '../../shared/supabase/supabaseClient';

const POLL_MS = 30_000;
const DEBOUNCE_MS = 500;

type Options = {
  enabled?: boolean;
};

/**
 * Live refresh for Mesas while the screen is focused.
 * Realtime on hospitality tables (debounce) + poll fallback.
 * Pull-to-refresh remains an escape hatch only.
 */
export function useMesasLiveRefresh(
  branchId: string | null | undefined,
  onChanged: () => void,
  options?: Options,
) {
  const enabled = options?.enabled !== false && Boolean(branchId?.trim());
  const [focused, setFocused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(true);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useFocusEffect(
    useCallback(() => {
      setFocused(true);
      return () => setFocused(false);
    }, []),
  );

  const active = enabled && focused;

  useEffect(() => {
    if (!active) return;

    const scheduleRefresh = () => {
      if (!visibleRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onChangedRef.current();
      }, DEBOUNCE_MS);
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    const branch = branchId!.trim();
    const channelName = `mesas-live-${branch}`;
    const filter = `branch_id=eq.${branch}`;

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;

      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          { event: '*', schema: 'hospitality', table: 'dining_tables', filter },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'hospitality', table: 'table_sessions', filter },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'hospitality', table: 'table_order_drafts', filter },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'hospitality', table: 'guest_signals', filter },
          scheduleRefresh,
        )
        .on(
          'postgres_changes',
          { event: '*', schema: 'hospitality', table: 'kitchen_tickets', filter },
          scheduleRefresh,
        )
        // presence no tiene branch_id; el poll cubre, pero el DELETE de "Salir" debe empujar ya.
        .on(
          'postgres_changes',
          { event: '*', schema: 'hospitality', table: 'guest_draft_presence' },
          scheduleRefresh,
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            scheduleRefresh();
          }
        });
    })();

    const pollId = setInterval(() => {
      if (!visibleRef.current) return;
      onChangedRef.current();
    }, POLL_MS);

    // First tick soon after focus so badges don't wait a full poll.
    scheduleRefresh();

    const appSub = AppState.addEventListener('change', (state) => {
      visibleRef.current = state === 'active';
      if (state === 'active') scheduleRefresh();
      if (state !== 'active' && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) scheduleRefresh();
        if (!visibleRef.current && timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      visibleRef.current = document.visibilityState === 'visible';
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      clearInterval(pollId);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (channel) void supabase.removeChannel(channel);
      appSub.remove();
      removeVisibility?.();
    };
  }, [active, branchId]);
}
